import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { EmptyState, InlineError, Select, Skeleton } from '../../components/ui';
import { createPublicSupabaseClient } from '../../lib/supabase';
import type { SupabaseClient } from '../../lib/supabase';
import { BriefBody } from '../ceoBrief/BriefBody';
import { BriefDealsSection } from '../ceoBrief/BriefDealsSection';
import { briefDateLabel } from '../ceoBrief/briefMarkdown';
import { BriefPortalError, briefPortalErrorMessage, loadBrief } from './briefPortalApi';
import type { BriefPortalPayload } from './briefPortalApi';
import './briefPortal.css';

/**
 * The CEO Brief, read without an account. Public, one URL, read-only.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Alex opened the brief on his phone, met a Google consent screen naming a
 * Supabase project ref and asking to send email as him, and closed it. Twice.
 * A brief nobody reads is a brief that was not written.
 *
 * ── It is the same page, not a copy of it ──────────────────────────────────
 *
 * Everything inside `.brief-page` is the Hub's own brief, reused: the class
 * pins the 4ocean palette, the grey ground, the card radius and the type
 * stack, and its comment in components.css says why — the Hub's --brand-blue
 * is a muted slate, the guidelines allow only #0057B8, and four themes must
 * not let a palette chosen for spreadsheet work decide the CEO's morning read.
 *
 * The first version of this page wrapped BriefBody in a container of its own
 * invention and lost all of that. It rendered grey-on-cream with hairline
 * dividers instead of white cards on a soft grey, and Kyu had to say twice
 * that the formatting was wrong before I stopped explaining and looked at the
 * two screenshots side by side. The lesson is the boring one: the styling
 * already existed under a name, and the job was to use the name.
 *
 * ── What it deliberately does not do ───────────────────────────────────────
 *
 * No sidebar, no shell, no navigation, no link back into the Hub. This route
 * is mounted outside every layout and every auth boundary. The holder of this
 * link is here to read one thing, and a door back would land them on the
 * sign-in screen this page exists to avoid.
 *
 * No Copy link button either, though the Hub's masthead has one. It would copy
 * an address that asks for a Google account.
 *
 * No editing. Not a hidden control, not a disabled button: the server has no
 * write path behind this token at all, and the page matches.
 */

/**
 * The ground `.brief-page` paints itself with.
 *
 * Set on the document too, so the strip either side of the 42rem column is not
 * whatever theme the reader's browser happened to remember. Duplicated from
 * components.css for the same reason theme.ts duplicates its canvases: nothing
 * can read a stylesheet value before it is needed here.
 */
const BRIEF_GROUND = '#f2f2f7';

export function BriefPortalPage({
  client: suppliedClient,
}: {
  /** Test seam. The live route always creates its own session-free client. */
  client?: SupabaseClient;
} = {}) {
  const [client] = useState(() => suppliedClient ?? createPublicSupabaseClient());
  const { token } = useParams<{ token: string }>();

  const [payload, setPayload] = useState<BriefPortalPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wanted, setWanted] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.backgroundColor;
    root.style.backgroundColor = BRIEF_GROUND;
    return () => {
      root.style.backgroundColor = previous;
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    if (token === undefined || token === '') {
      setError(briefPortalErrorMessage('bad_request'));
      setLoading(false);
      return;
    }
    setLoading(true);
    void loadBrief(client, token, wanted === '' ? undefined : wanted)
      .then((next) => {
        if (disposed) {
          return;
        }
        setPayload(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (disposed) {
          return;
        }
        setError(
          briefPortalErrorMessage(cause instanceof BriefPortalError ? cause.code : 'unreachable'),
        );
      })
      .finally(() => {
        if (!disposed) {
          setLoading(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, [client, token, wanted]);

  return (
    <main className="brief-page brief-portal">
      {error !== null ? (
        <InlineError>{error}</InlineError>
      ) : loading && payload === null ? (
        <Skeleton lines={6} label="Loading the brief" />
      ) : payload === null || payload.brief === null ? (
        <EmptyState
          title="No brief yet"
          description="Nothing has been written. This link stays live, so it is worth checking back."
        />
      ) : (
        <>
          <div className="brief-masthead">
            <p className="brief-eyebrow">4ocean · TAP Division</p>
            <h1 className="brief-date">{briefDateLabel(payload.brief.brief_date)}</h1>
          </div>

          {payload.dates.length > 1 ? (
            <div className="brief-portal-picker">
              <Select
                id="brief-portal-date"
                label="Showing"
                value={payload.brief.brief_date}
                options={payload.dates.map((date) => ({
                  value: date,
                  label: briefDateLabel(date),
                }))}
                onChange={(event) => {
                  setWanted(event.target.value);
                }}
              />
            </div>
          ) : null}

          <div aria-busy={loading} className="brief-portal-reading">
            <BriefBody markdown={payload.brief.body} />
            <BriefDealsSection deals={payload.deals} variant="portal" />
          </div>

          <p className="brief-portal-foot">
            This is a private link. It can be switched off at any time.
          </p>
        </>
      )}
    </main>
  );
}
